/**
 * SendPollDialog — Criar ENQUETE na conversa.
 *
 * Só canais conectados por QR Code (gateway-whatsapp, engines Node e Go)
 * suportam enquete: a WhatsApp Cloud API (canal oficial Meta) não tem esse
 * recurso, então o item nem aparece no menu nesses canais.
 *
 * Limites do WhatsApp: 2 a 12 opções; `selectableCount` nunca maior que o
 * número de opções.
 */

import React, { useEffect, useMemo, useState } from 'react';
import type { MessageContent } from '@troia-v3/types';
import { Button, Input, Switch } from '@/shared/components/ui';
import { Modal, ModalHeader, ModalFooter } from '@/shared/components/ui/Modal';
import { Icon } from '@/shared/icons';

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 12;
const MAX_QUESTION = 255;
const MAX_OPTION = 100;

interface SendPollDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (content: MessageContent[]) => void;
  isLoading?: boolean;
}

const SendPollDialog: React.FC<SendPollDialogProps> = ({ isOpen, onClose, onSend, isLoading = false }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);

  // Reset-on-close: o ChatBox monta o dialog uma vez só; sem isso a enquete
  // anterior reaparece ao reabrir (mesmo achado da revisão de 2026-08-19).
  useEffect(() => {
    if (!isOpen) {
      setQuestion('');
      setOptions(['', '']);
      setAllowMultiple(false);
    }
  }, [isOpen]);

  const filledOptions = useMemo(
    () => options.map((o) => o.trim()).filter((o) => o.length > 0),
    [options],
  );
  const hasDuplicates = useMemo(
    () => new Set(filledOptions).size !== filledOptions.length,
    [filledOptions],
  );
  const canSend = question.trim().length > 0 && filledOptions.length >= MIN_OPTIONS && !hasDuplicates;

  const updateOption = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? value.slice(0, MAX_OPTION) : o)));
  };

  const addOption = () => {
    setOptions((prev) => (prev.length >= MAX_OPTIONS ? prev : [...prev, '']));
  };

  const removeOption = (index: number) => {
    setOptions((prev) => (prev.length <= MIN_OPTIONS ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSend = () => {
    if (!canSend) return;
    const poll: MessageContent = {
      type: 'poll',
      name: question.trim(),
      options: filledOptions,
      // 1 = escolha única. Múltipla = qualquer quantidade até o total.
      selectableCount: allowMultiple ? filledOptions.length : 1,
    };
    onSend([poll]);
  };

  const handleClose = () => {
    setQuestion('');
    setOptions(['', '']);
    setAllowMultiple(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="md" closeOnEscape={!isLoading}>
      <div data-testid="send-poll-dialog">
        <ModalHeader title="Criar enquete" onClose={isLoading ? undefined : handleClose} />

        <div className="px-5 pt-3 pb-1">
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#334155' }}>Pergunta</label>
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value.slice(0, MAX_QUESTION))}
            placeholder="Sobre o que é a enquete?"
            data-testid="send-poll-question-input"
          />
        </div>

        <div className="px-5 pt-3">
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#334155' }}>Opções</label>
          <div className="flex flex-col gap-2" data-testid="send-poll-options">
            {options.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                  placeholder={`Opção ${index + 1}`}
                  data-testid={`send-poll-option-input-${index}`}
                />
                <button
                  type="button"
                  onClick={() => removeOption(index)}
                  disabled={options.length <= MIN_OPTIONS}
                  className="w-[38px] h-[38px] rounded-lg flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ color: '#94a3b8' }}
                  title="Remover opção"
                  data-testid={`send-poll-remove-option-${index}`}
                >
                  <Icon name="trash" className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {options.length < MAX_OPTIONS && (
            <button
              type="button"
              onClick={addOption}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium"
              style={{ color: '#8b5cf6', minHeight: 38 }}
              data-testid="send-poll-add-option"
            >
              <Icon name="plus" className="w-4 h-4" />
              Adicionar opção
            </button>
          )}

          {hasDuplicates && (
            <p className="text-xs mt-1.5" style={{ color: '#dc2626' }} data-testid="send-poll-duplicate-warning">
              Há opções repetidas — cada opção precisa ser diferente.
            </p>
          )}
        </div>

        <div className="px-5 pt-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium m-0" style={{ color: '#334155' }}>Permitir mais de uma resposta</p>
            <p className="text-xs m-0 mt-0.5" style={{ color: '#94a3b8' }}>
              {allowMultiple ? 'Cada pessoa pode marcar várias opções' : 'Cada pessoa escolhe só uma opção'}
            </p>
          </div>
          <Switch
            checked={allowMultiple}
            onChange={setAllowMultiple}
            data-testid="send-poll-multiple-switch"
          />
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading} data-testid="send-poll-cancel">
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!canSend || isLoading} data-testid="send-poll-confirm">
            {isLoading ? 'Enviando…' : 'Enviar enquete'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default SendPollDialog;
